// Customer communication — spec ch.06: "Draft list, reviewed message, edit and
// approve/send … Versioned recipients/content, durable send operation, outcome
// reconciliation and incoming reply association. Add waiting/reply/closed states
// within this screen; an email send is not task completion".
//
// That last clause is the screen. Everything here is arranged so that no part
// of it can be read as "the customer has the message" or "the missing document
// arrived".
//
// ---------------------------------------------------------------------------
// Four rules
// ---------------------------------------------------------------------------
//
// **The recipient is not a text field.** A reviewer picks a CONTACT the office
// has recorded, and the address comes from the server. There is no input on
// this page into which an address can be typed and sent to — correcting an
// address is a separate act, on the contacts card, and it makes the contact
// unverified again.
//
// **Unverified is a state with words, not a disabled button.** ch.02's
// "verified recipient/contact version" means an office has to have checked the
// address. So a request addressed to an unverified contact shows what is
// missing and who can fix it, next to the control that would verify it, rather
// than a greyed-out Send with no explanation.
//
// **Approving is not sending, and this build sends nothing.** approve-send
// answers 202 and an operation id; the operation is what says whether anything
// left. In this deployment nothing does — there is no mail transport, the
// worker refuses the job by name, and the operation lands in `failed_safe`.
// The screen says that in the banner and shows the operation's real state
// rather than a hopeful one. An office that approves a message and finds the
// customer never got it deserves to have been told which of those two facts it
// was, before approving.
//
// **A reply is an association, not an answer.** Attaching an arrival moves the
// request to `reply_received` and says, in words, that somebody still has to
// look at whether it is the right document. ch.02: "Receiving a reply does not
// approve a booking or prove the missing evidence is complete."
//
// ---------------------------------------------------------------------------
// Where a request comes from
// ---------------------------------------------------------------------------
//
// A request is always ABOUT something — a document or a bank line — so it is
// created where that something is looked at. Bank matching hands this screen a
// subject and it opens with the form filled in. There is no "new request"
// button that invents a subject, because a request with no source is a task
// nobody can close.


import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { ApiError, newIdempotencyKey, request, tenantPath } from '../api/client.ts';
import type {
  Administration,
  Contact,
  Contacts,
  CustomerRequest,
  CustomerRequests,
  RequestDetail,
} from '../api/types.ts';
import { EmptyState } from '../components/EmptyState.tsx';
import { StateBadge } from '../components/StateBadge.tsx';
import { StepTracker, type Step } from '../components/StepTracker.tsx';
import type { ScreenId } from '../components/Shell.tsx';
import { REQUEST_STATES, DOCUMENT_STATES, OPERATION_STATES, useStates } from '../states.ts';
import { administrationFilter, useWorkspace } from '../state/workspace.tsx';
import { translate, useT, type Translate } from '../i18n/index.tsx';
import * as format from '../format.ts';
import { Icon } from '../components/Icon.tsx';

function requestSteps(t: Translate): readonly Step[] {
  return [
    { label: t('msg.step.written'), icon: 'sparkle' },
    { label: t('msg.step.approved'), icon: 'user' },
    { label: t('msg.step.sending'), icon: 'send' },
    { label: t('msg.step.sent'), icon: 'check' },
  ];
}

/**
 * How far the request's state PROVES it got. Only `sent` — the transport
 * accepted it — fills the last step. Every other state after approval
 * (delivery unknown, waiting, a reply, resolved, cancelled) is not ordered
 * against sending by anything the API enforces, so it gets no tracker rather
 * than one that implies a message left.
 */
function requestProgress(state: string, written: boolean): { reached: number; moving: boolean } | null {
  if (state === 'draft') return { reached: written ? 1 : 0, moving: false };
  if (state === 'approved') return { reached: 2, moving: false };
  if (state === 'sending') return { reached: 2, moving: true };
  if (state === 'sent') return { reached: 4, moving: false };
  return null;
}

/** Roles the API lets draft, approve and attach. Anything else is told why. */
const MAY_ACT = new Set(['owner', 'reviewer']);

/** What Bank matching (or any other screen) hands over to start a request. */
export interface RequestSubject {
  readonly kind: 'bank_line' | 'document';
  readonly id: string;
  readonly administrationId: string;
  /** What the reviewer was looking at, so the form is recognisable. */
  readonly label: string;
}

type Section<T> =
  | { readonly kind: 'loading' }
  | { readonly kind: 'error'; readonly message: string }
  | { readonly kind: 'ready'; readonly data: T };

export interface CustomerCommunicationProps {
  /** A subject handed over by another screen, or null. */
  readonly subject: RequestSubject | null;
  /** Called once the subject has been turned into a request, or dismissed. */
  readonly onSubjectHandled: () => void;
  readonly onOpenDocument: (documentId: string) => void;
  /** Where the empty page sends the reviewer: Bank matching. */
  readonly onNavigate?: (screen: ScreenId) => void;
}

export function CustomerCommunicationScreen({
  subject,
  onSubjectHandled,
  onOpenDocument,
  onNavigate,
}: CustomerCommunicationProps): ReactNode {
  const workspace = useWorkspace();
  const t = useT();
  const { token, administrations, administrationId } = workspace;
  const tenantId = workspace.membership.tenantId;
  const role = workspace.membership.role;
  const mayAct = MAY_ACT.has(role);
  const filter = administrationFilter(administrationId);

  const [requests, setRequests] = useState<Section<CustomerRequests>>({ kind: 'loading' });
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // The contacts card needs ONE administration: a contact belongs to one, and a
  // list across several would offer a reviewer an address for the wrong
  // customer. It starts at the subject's administration when there is one
  // (that is what the reviewer is working on), else the selected one, and the
  // card's own dropdown changes it.
  const initialContacts = subject?.administrationId ?? filter ?? null;
  const [contactsFor, setContactsFor] = useState<string | null>(initialContacts);
  useEffect(() => setContactsFor(initialContacts), [initialContacts]);

  const [contacts, setContacts] = useState<Section<Contacts>>({ kind: 'loading' });

  const query = filter === undefined ? '' : `?administrationId=${encodeURIComponent(filter)}`;

  const loadRequests = useCallback(async () => {
    try {
      setRequests({
        kind: 'ready',
        data: await request<CustomerRequests>(tenantPath(tenantId, `/requests${query}`), { token }),
      });
    } catch (caught) {
      setRequests({ kind: 'error', message: sectionMessage(caught, t) });
    }
    // t is left out on purpose: a language switch must not re-read.
  }, [tenantId, token, query]);

  const loadContacts = useCallback(async () => {
    if (contactsFor === null) {
      setContacts({ kind: 'ready', data: { administrationId: '', contacts: [] } });
      return;
    }
    try {
      setContacts({
        kind: 'ready',
        data: await request<Contacts>(
          tenantPath(tenantId, `/administrations/${encodeURIComponent(contactsFor)}/contacts`),
          { token },
        ),
      });
    } catch (caught) {
      setContacts({ kind: 'error', message: sectionMessage(caught, t) });
    }
  }, [tenantId, token, contactsFor]);

  useEffect(() => {
    // Reset first: another office's requests must not stay on screen under the
    // new office's name while the next request is in flight.
    setRequests({ kind: 'loading' });
    setSelectedId(null);
    setNotice(null);
    void loadRequests();
  }, [loadRequests]);

  useEffect(() => {
    setContacts({ kind: 'loading' });
    void loadContacts();
  }, [loadContacts]);

  const act = useCallback(
    async (work: () => Promise<string>): Promise<void> => {
      setBusy(true);
      setNotice(null);
      try {
        setNotice(await work());
      } catch (caught) {
        setNotice(actionMessage(caught, t));
      } finally {
        setBusy(false);
      }
    },
    [t],
  );

  const createRequest = useCallback(
    (contactId: string) =>
      act(async () => {
        if (subject === null) return t('msg.nothingToAsk');
        const body = await request<{ request: CustomerRequest; created: boolean }>(
          tenantPath(tenantId, '/requests'),
          {
            method: 'POST',
            token,
            idempotencyKey: newIdempotencyKey(),
            body: {
              ...(subject.kind === 'document'
                ? { documentId: subject.id }
                : { bankLineId: subject.id }),
              contactId,
              purpose: 'missing_document',
            },
          },
        );
        await loadRequests();
        setSelectedId(body.request.id);
        onSubjectHandled();
        return body.created ? t('msg.created') : t('msg.createdExisting');
      }),
    [act, subject, tenantId, token, loadRequests, onSubjectHandled, t],
  );

  const addContact = useCallback(
    (address: string, displayName: string) =>
      act(async () => {
        if (contactsFor === null) return t('msg.selectAdministration');
        await request(
          tenantPath(tenantId, `/administrations/${encodeURIComponent(contactsFor)}/contacts`),
          {
            method: 'POST',
            token,
            idempotencyKey: newIdempotencyKey(),
            body: {
              address,
              ...(displayName === '' ? {} : { displayName }),
              purposes: ['missing_document'],
            },
          },
        );
        await loadContacts();
        return t('msg.contactAdded');
      }),
    [act, contactsFor, tenantId, token, loadContacts, t],
  );

  const verifyContact = useCallback(
    (contact: Contact, method: string) =>
      act(async () => {
        await request(tenantPath(tenantId, `/contacts/${encodeURIComponent(contact.contactId)}/verify`), {
          method: 'POST',
          token,
          idempotencyKey: newIdempotencyKey(),
          body: { expectedRevision: contact.version, method },
        });
        await loadContacts();
        await loadRequests();
        return t('msg.contactVerified');
      }),
    [act, tenantId, token, loadContacts, loadRequests, t],
  );

  const reload = useCallback(async () => {
    await loadRequests();
  }, [loadRequests]);

  const list = requests.kind === 'ready' ? requests.data.requests : [];
  const selected = useMemo(
    () => list.find((entry) => entry.id === selectedId) ?? null,
    [list, selectedId],
  );
  // Nothing asked yet and nothing being started: one empty state for the page,
  // not an empty list beside an empty detail.
  const nothingYet = requests.kind === 'ready' && list.length === 0 && subject === null;

  // The new-request form only offers contacts of the subject's administration.
  const subjectContacts: Section<Contacts> =
    subject !== null && contacts.kind === 'ready' && contacts.data.administrationId !== subject.administrationId
      ? { kind: 'ready', data: { administrationId: subject.administrationId, contacts: [] } }
      : contacts;

  return (
    <div className="messages" data-testid="customer-communication">
      <header className="page-head">
        <h1>{t('msg.title')}</h1>
        <p>{t('msg.intro')}</p>
      </header>

      <div className="notice" data-testid="no-transport">
        <span className="icon-green" aria-hidden="true">
          <Icon name="lock" size={18} />
        </span>
        <div className="grow">{t('msg.noTransport')}</div>
      </div>

      {notice === null ? null : (
        <div className="notice" role="status" data-testid="messages-notice">
          <div className="grow">{notice}</div>
        </div>
      )}

      {subject === null ? null : (
        <NewRequestCard
          subject={subject}
          administrations={administrations}
          contacts={subjectContacts}
          busy={busy}
          mayAct={mayAct}
          role={role}
          onCreate={createRequest}
          onCancel={onSubjectHandled}
        />
      )}

      {nothingYet ? (
        <section className="dash-card messages-empty" data-testid="messages-empty">
          <span className="tile big neutral" aria-hidden="true">
            <Icon name="chat" size={24} />
          </span>
          <h2>{t('msg.empty.title')}</h2>
          <p>{t('msg.empty.line')}</p>
          {onNavigate === undefined ? null : (
            <button type="button" className="btn primary" data-testid="messages-to-bank" onClick={() => onNavigate('bank')}>
              {t('msg.empty.action')}
            </button>
          )}
        </section>
      ) : (
        <div className="workbench">
          <div className="card">
            <div className="card-head">
              <div>
                <h2>{t('msg.list.title')}</h2>
                <p>{t('msg.list.sub')}</p>
              </div>
              <span className="badge">{list.length}</span>
            </div>

            {requests.kind === 'error' ? (
              <div className="card-body">
                <p className="error-banner" role="alert">
                  {requests.message}
                </p>
              </div>
            ) : requests.kind === 'loading' ? (
              <div className="empty">
                <p>{t('msg.list.reading')}</p>
              </div>
            ) : list.length === 0 ? (
              <div className="empty">
                <h3>{t('msg.empty.title')}</h3>
                <p>{t('msg.empty.line')}</p>
              </div>
            ) : (
              list.map((entry) => (
                <button
                  key={entry.id}
                  type="button"
                  className={`row${entry.id === selectedId ? ' selected' : ''}`}
                  data-testid={`request-${entry.id}`}
                  onClick={() => setSelectedId(entry.id)}
                >
                  <div className="grow">
                    <strong>{entry.draft?.subject ?? t('msg.noSubject')}</strong>
                    <p className="muted small">
                      {administrationName(administrations, entry.administrationId)} ·{' '}
                      {entry.contact.displayName ?? entry.contact.address}
                      {entry.contact.verification === 'verified' ? '' : t('msg.addressUnverified')}
                    </p>
                  </div>
                  <StateBadge table={REQUEST_STATES} state={entry.state} />
                </button>
              ))
            )}
          </div>

          {selected === null ? (
            <div className="card">
              <div className="empty">
                <h3>{t('msg.select.title')}</h3>
                <p>{t('msg.select.text')}</p>
              </div>
            </div>
          ) : (
            <RequestDetailCard
              key={selected.id}
              summary={selected}
              tenantId={tenantId}
              token={token}
              busy={busy}
              mayAct={mayAct}
              role={role}
              act={act}
              onChanged={reload}
              onOpenDocument={onOpenDocument}
            />
          )}
        </div>
      )}

      <ContactsCard
        state={contacts}
        administrationId={contactsFor}
        administrations={administrations}
        busy={busy}
        mayAct={mayAct}
        role={role}
        onPick={setContactsFor}
        onAdd={addContact}
        onVerify={verifyContact}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Starting a request, from a subject another screen was looking at.
// ---------------------------------------------------------------------------

function NewRequestCard({
  subject,
  administrations,
  contacts,
  busy,
  mayAct,
  role,
  onCreate,
  onCancel,
}: {
  readonly subject: RequestSubject;
  readonly administrations: readonly Administration[];
  readonly contacts: Section<Contacts>;
  readonly busy: boolean;
  readonly mayAct: boolean;
  readonly role: string;
  readonly onCreate: (contactId: string) => void;
  readonly onCancel: () => void;
}): ReactNode {
  const t = useT();
  const available = contacts.kind === 'ready' ? contacts.data.contacts : [];
  const [contactId, setContactId] = useState('');

  return (
    <div className="card" data-testid="new-request">
      <div className="card-head">
        <div>
          <h2>{t('msg.new.title')}</h2>
          <p>
            {subject.kind === 'bank_line' ? t('msg.new.bankLine') : t('msg.new.document')} ·{' '}
            {subject.label} · {administrationName(administrations, subject.administrationId)}
          </p>
        </div>
      </div>
      <div className="card-body">
        {!mayAct ? (
          <p className="muted">{t('msg.new.roleCannot', { role: role.replace('_', ' ') })}</p>
        ) : available.length === 0 ? (
          <p className="muted">{t('msg.new.noContacts')}</p>
        ) : (
          <>
            <label className="field">
              {t('msg.new.ask')}
              <select value={contactId} onChange={(event) => setContactId(event.target.value)}>
                <option value="">{t('msg.new.choose')}</option>
                {available.map((contact) => (
                  <option key={contact.contactId} value={contact.contactId}>
                    {contact.displayName ?? contact.address} ({contact.address})
                    {contact.verification === 'verified' ? '' : t('msg.new.notVerified')}
                  </option>
                ))}
              </select>
            </label>
            {/* Deliberately NOT a filter on the list above. A reviewer may
                prepare an ask to a contact nobody has confirmed yet; what they
                cannot do is approve it, and that is where the refusal lives. */}
            <p className="muted small">{t('msg.new.unverifiedNote')}</p>
          </>
        )}
      </div>
      <div className="card-foot">
        <button type="button" className="btn text" onClick={onCancel}>
          {t('msg.new.notNow')}
        </button>
        <button
          type="button"
          className="btn primary"
          disabled={busy || !mayAct || contactId === ''}
          onClick={() => onCreate(contactId)}
        >
          {t('msg.new.open')}
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// One request: the message, the approval, and everything that came back.
// ---------------------------------------------------------------------------

function RequestDetailCard({
  summary,
  tenantId,
  token,
  busy,
  mayAct,
  role,
  act,
  onChanged,
  onOpenDocument,
}: {
  readonly summary: CustomerRequest;
  readonly tenantId: string;
  readonly token: string;
  readonly busy: boolean;
  readonly mayAct: boolean;
  readonly role: string;
  readonly act: (work: () => Promise<string>) => Promise<void>;
  readonly onChanged: () => Promise<void>;
  readonly onOpenDocument: (documentId: string) => void;
}): ReactNode {
  const t = useT();
  const states = useStates();
  const [detail, setDetail] = useState<Section<RequestDetail>>({ kind: 'loading' });

  const load = useCallback(async () => {
    try {
      setDetail({
        kind: 'ready',
        data: await request<RequestDetail>(
          tenantPath(tenantId, `/requests/${encodeURIComponent(summary.id)}`),
          { token },
        ),
      });
    } catch (caught) {
      setDetail({ kind: 'error', message: sectionMessage(caught, t) });
    }
  }, [tenantId, token, summary.id]);

  useEffect(() => {
    void load();
  }, [load]);

  const refresh = useCallback(async () => {
    await load();
    await onChanged();
  }, [load, onChanged]);

  if (detail.kind === 'error') {
    return (
      <div className="card">
        <div className="card-body">
          <p className="error-banner" role="alert">
            {detail.message}
          </p>
        </div>
      </div>
    );
  }
  if (detail.kind === 'loading') {
    return (
      <div className="card">
        <div className="empty">
          <p>{t('msg.detail.reading')}</p>
        </div>
      </div>
    );
  }

  const { request: view, revisions, mailEvents, replies } = detail.data;
  const current = revisions.find((revision) => revision.id === view.draft?.revisionId) ?? null;
  const blocked = blockedReason(view, role, t);
  const progress = requestProgress(view.state, view.draft !== null);

  return (
    <div className="card" data-testid="request-detail">
      <div className="card-head">
        <div>
          <h2>{view.draft?.subject ?? t('msg.noSubject')}</h2>
          <p className="muted small">
            {t('msg.detail.to', { name: view.contact.displayName ?? view.contact.address })} ·{' '}
            <span data-testid="detail-address">{view.contact.address}</span>
          </p>
        </div>
        <StateBadge table={REQUEST_STATES} state={view.state} />
      </div>

      <div className="card-body">
        <p className="muted small">{states.display(REQUEST_STATES, view.state).explanation}</p>
        {progress === null ? null : <StepTracker steps={requestSteps(t)} {...progress} />}

        {view.contact.verification === 'verified' ? null : (
          <p className="callout" data-testid="unverified-warning">
            {t('msg.detail.unverified')}
          </p>
        )}

        {view.draft !== null && !view.draft.addressIsCurrent ? (
          <p className="callout" data-testid="address-moved">
            {t('msg.detail.addressMoved')}
          </p>
        ) : null}

        {view.sendOperation === null ? null : (
          <p data-testid="send-operation">
            {t('msg.detail.operation')}{' '}
            <StateBadge table={OPERATION_STATES} state={view.sendOperation.state} />{' '}
            <span className="muted small">
              {states.display(OPERATION_STATES, view.sendOperation.state).explanation}
            </span>
          </p>
        )}
      </div>

      {view.state === 'draft' ? (
        <DraftEditor
          view={view}
          initialSubject={current?.subject ?? ''}
          initialBody={current?.body ?? ''}
          busy={busy}
          mayAct={mayAct}
          role={role}
          blocked={blocked}
          onSave={(subject, body) =>
            act(async () => {
              await request(tenantPath(tenantId, `/requests/${encodeURIComponent(view.id)}/draft`), {
                method: 'PATCH',
                token,
                idempotencyKey: newIdempotencyKey(),
                body: { expectedRevision: view.revision, subject, body },
              });
              await refresh();
              return t('msg.saved');
            })
          }
          onApprove={() =>
            act(async () => {
              if (current === null) return t('msg.writeFirst');
              const answer = await request<{ operationId: string }>(
                tenantPath(tenantId, `/requests/${encodeURIComponent(view.id)}/approve-send`),
                {
                  method: 'POST',
                  token,
                  idempotencyKey: newIdempotencyKey(),
                  body: {
                    expectedRevision: view.revision,
                    // The exact revision and the exact body this screen showed.
                    // A message edited in another tab answers 409 rather than
                    // being approved unread.
                    messageRevisionId: current.id,
                    bodyHash: current.bodyHash,
                    decision: 'approve_send',
                  },
                },
              );
              await refresh();
              return t('msg.approved', { id: answer.operationId });
            })
          }
        />
      ) : null}

      <AttachReply
        view={view}
        busy={busy}
        mayAct={mayAct}
        onAttach={(documentId, note, threadRef) =>
          act(async () => {
            await request(
              tenantPath(tenantId, `/requests/${encodeURIComponent(view.id)}/attach-reply`),
              {
                method: 'POST',
                token,
                idempotencyKey: newIdempotencyKey(),
                body: {
                  expectedRevision: view.revision,
                  documentId,
                  ...(note === '' ? {} : { note }),
                  ...(threadRef === '' ? {} : { providerThreadRef: threadRef }),
                },
              },
            );
            await refresh();
            return t('msg.attached');
          })
        }
      />

      {replies.length === 0 ? null : (
        <div className="card-body">
          <h3 style={{ marginBottom: 8 }}>{t('msg.replies.title')}</h3>
          {replies.map((reply) => (
            <div className="row" key={reply.id} data-testid={`reply-${reply.id}`}>
              <div className="grow">
                <strong>
                  <StateBadge table={DOCUMENT_STATES} state={reply.documentState} />
                </strong>
                <p className="muted small">
                  {reply.note ?? t('msg.replies.noNote')} · {format.dateTime(reply.createdAt)}
                  {/* Never "this is the invoice": the association is somebody's
                      judgement and the document is read like any other. */}
                  {reply.proposalId === null
                    ? t('msg.replies.noProposal')
                    : t('msg.replies.proposal', { status: reply.proposalStatus ?? t('msg.replies.prepared') })}
                </p>
              </div>
              <button type="button" className="btn text" onClick={() => onOpenDocument(reply.documentId)}>
                {t('msg.replies.open')}
              </button>
            </div>
          ))}
        </div>
      )}

      {mailEvents.length === 0 ? null : (
        <div className="card-body">
          <h3 style={{ marginBottom: 8 }}>{t('msg.events.title')}</h3>
          <ul className="note-list">
            {mailEvents.map((event) => (
              <li key={event.id} data-testid={`mail-event-${event.id}`}>
                {states.mailEvent(event.kind)} · {format.dateTime(event.occurredAt)}
                {event.providerThreadRef === null ? '' : t('msg.events.thread', { ref: event.providerThreadRef })}
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="card-body">
        <h3 style={{ marginBottom: 8 }}>{t('msg.revisions.title')}</h3>
        {revisions.length === 0 ? (
          <p className="muted small">{t('msg.revisions.none')}</p>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>#</th>
                  <th>{t('msg.revisions.subject')}</th>
                  <th>{t('msg.revisions.to')}</th>
                  <th>{t('msg.revisions.written')}</th>
                  <th>{t('msg.revisions.approved')}</th>
                </tr>
              </thead>
              <tbody>
                {revisions.map((revision) => (
                  <tr key={revision.id} data-testid={`revision-${revision.id}`}>
                    <td>{revision.revision}</td>
                    <td>{revision.subject}</td>
                    {/* The recipient AS STORED on that revision, which is the
                        address that would have gone out — not whatever the
                        contact says today. */}
                    <td className="muted small">{revision.recipients.join(', ')}</td>
                    <td className="muted small">{format.dateTime(revision.createdAt)}</td>
                    <td className="muted small">
                      {revision.approvedAt === null ? '—' : format.dateTime(revision.approvedAt)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <p className="muted small" style={{ marginTop: 10 }}>
          {t('msg.revisions.note')}
        </p>
      </div>
    </div>
  );
}

const english: Translate = (key, vars) => translate('en', key, vars);

/** Why this request cannot be approved right now, or null. English unless given a `t`. */
export function blockedReason(view: CustomerRequest, role: string, t: Translate = english): string | null {
  if (!MAY_ACT.has(role)) return t('msg.blocked.role', { role: role.replace('_', ' ') });
  if (view.draft === null) return t('msg.writeFirst');
  if (view.contact.verification !== 'verified') return t('msg.blocked.unverified');
  if (!view.draft.addressIsCurrent) return t('msg.blocked.moved');
  return null;
}

function DraftEditor({
  view,
  initialSubject,
  initialBody,
  busy,
  mayAct,
  role,
  blocked,
  onSave,
  onApprove,
}: {
  readonly view: CustomerRequest;
  readonly initialSubject: string;
  readonly initialBody: string;
  readonly busy: boolean;
  readonly mayAct: boolean;
  readonly role: string;
  readonly blocked: string | null;
  readonly onSave: (subject: string, body: string) => void;
  readonly onApprove: () => void;
}): ReactNode {
  const t = useT();
  const [subject, setSubject] = useState(initialSubject);
  const [body, setBody] = useState(initialBody);

  if (!mayAct) {
    return (
      <div className="card-body">
        <p className="muted" data-testid="draft-role-refusal">
          {t('msg.draft.roleCannot', { role: role.replace('_', ' ') })}
        </p>
      </div>
    );
  }

  return (
    <div className="card-body">
      <h3 style={{ marginBottom: 8 }}>{t('msg.draft.title')}</h3>
      <label className="field">
        {t('msg.draft.subject')}
        <input value={subject} onChange={(event) => setSubject(event.target.value)} />
      </label>
      <label className="field">
        {t('msg.draft.body')}
        <textarea rows={6} value={body} onChange={(event) => setBody(event.target.value)} />
      </label>
      {/* No recipient input, and none is coming. The address is resolved by the
          server from the contact, and the API refuses a `recipients` field. */}
      <p className="muted small">{t('msg.draft.goesTo', { address: view.contact.address })}</p>
      <div className="card-foot">
        <button
          type="button"
          className="btn"
          disabled={busy || subject.trim() === '' || body.trim() === ''}
          onClick={() => onSave(subject, body)}
        >
          {t('msg.draft.save')}
        </button>
        <button
          type="button"
          className="btn primary"
          data-testid="approve-send"
          disabled={busy || blocked !== null}
          title={blocked ?? t('msg.draft.approveTitle')}
          onClick={onApprove}
        >
          {t('msg.draft.approve')}
        </button>
      </div>
      {blocked === null ? null : (
        <p className="muted small" data-testid="approve-blocked">
          {blocked}
        </p>
      )}
    </div>
  );
}

function AttachReply({
  view,
  busy,
  mayAct,
  onAttach,
}: {
  readonly view: CustomerRequest;
  readonly busy: boolean;
  readonly mayAct: boolean;
  readonly onAttach: (documentId: string, note: string, threadRef: string) => void;
}): ReactNode {
  const t = useT();
  const [documentId, setDocumentId] = useState('');
  const [note, setNote] = useState('');
  const [threadRef, setThreadRef] = useState('');

  if (view.state === 'draft') {
    return (
      <div className="card-body">
        <p className="muted small" data-testid="nothing-to-reply-to">
          {t('msg.reply.nothing')}
        </p>
      </div>
    );
  }
  if (!mayAct) return null;

  return (
    <div className="card-body">
      <h3 style={{ marginBottom: 8 }}>{t('msg.reply.title')}</h3>
      <p className="muted small">{t('msg.reply.note')}</p>
      <div className="form-row">
        <label className="field">
          {t('msg.reply.document')}
          <input
            value={documentId}
            placeholder={t('msg.reply.documentPlaceholder')}
            onChange={(event) => setDocumentId(event.target.value)}
          />
        </label>
        <label className="field">
          {t('msg.reply.thread')}
          <input value={threadRef} onChange={(event) => setThreadRef(event.target.value)} />
        </label>
      </div>
      <label className="field">
        {t('msg.reply.why')}
        <input value={note} onChange={(event) => setNote(event.target.value)} />
      </label>
      <div className="card-foot">
        <button
          type="button"
          className="btn"
          data-testid="attach-reply"
          disabled={busy || documentId.trim() === ''}
          onClick={() => onAttach(documentId.trim(), note, threadRef)}
        >
          {t('msg.reply.attach')}
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Contacts: who one administration may be written to.
// ---------------------------------------------------------------------------

function ContactsCard({
  state,
  administrationId,
  administrations,
  busy,
  mayAct,
  role,
  onPick,
  onAdd,
  onVerify,
}: {
  readonly state: Section<Contacts>;
  readonly administrationId: string | null;
  readonly administrations: readonly Administration[];
  readonly busy: boolean;
  readonly mayAct: boolean;
  readonly role: string;
  readonly onPick: (administrationId: string | null) => void;
  readonly onAdd: (address: string, displayName: string) => void;
  readonly onVerify: (contact: Contact, method: string) => void;
}): ReactNode {
  const t = useT();
  const [address, setAddress] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [verifying, setVerifying] = useState<string | null>(null);
  const [method, setMethod] = useState('');

  return (
    <section className="dash-card rule-block" data-testid="contacts">
      <div className="rule-head">
        <span className="tile rule-tile green" aria-hidden="true">
          <Icon name="users" size={20} />
        </span>
        <div className="rule-head-main">
          <h2>{t('msg.contacts.title')}</h2>
          {/* A contact belongs to one administration: this picks which. */}
          <select
            className="head-select"
            aria-label={t('msg.contacts.pick')}
            data-testid="contacts-administration"
            value={administrationId ?? ''}
            onChange={(event) => onPick(event.target.value === '' ? null : event.target.value)}
          >
            <option value="">{t('msg.contacts.choose')}</option>
            {administrations.map((administration) => (
              <option key={administration.id} value={administration.id}>
                {administration.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      {administrationId === null ? (
        <p className="rule-line" data-testid="contacts-pick">
          {t('msg.contacts.pickLine')}
        </p>
      ) : state.kind === 'error' ? (
        <p className="error-banner" role="alert">
          {state.message}
        </p>
      ) : state.kind === 'loading' ? (
        <p className="dash-loading">{t('msg.contacts.reading')}</p>
      ) : (
        <>
          {state.data.contacts.length === 0 ? (
            <p className="rule-line">{t('msg.contacts.none')}</p>
          ) : (
            state.data.contacts.map((contact) => (
              <div className="rule-row" key={contact.contactId} data-testid={`contact-${contact.contactId}`}>
                <div className="grow">
                  <strong>{contact.displayName ?? contact.address}</strong>
                  <p>
                    {contact.address} · {t('msg.contacts.version', { version: contact.version })} ·{' '}
                    {contact.verification === 'verified'
                      ? t('msg.contacts.verifiedHow', { method: contact.verificationMethod ?? t('msg.contacts.noAccount') })
                      : t('msg.contacts.notVerifiedLower')}
                  </p>
                  {verifying === contact.contactId ? (
                    <div className="contact-verify">
                      <label className="field">
                        {t('msg.contacts.howLabel')}
                        <input
                          value={method}
                          placeholder={t('msg.contacts.howPlaceholder')}
                          onChange={(event) => setMethod(event.target.value)}
                        />
                      </label>
                      <button
                        type="button"
                        className="btn primary"
                        data-testid={`confirm-verify-${contact.contactId}`}
                        disabled={busy || method.trim().length < 3}
                        onClick={() => {
                          onVerify(contact, method.trim());
                          setVerifying(null);
                          setMethod('');
                        }}
                      >
                        {t('msg.contacts.record')}
                      </button>
                    </div>
                  ) : null}
                </div>
                <span className={`badge ${contact.verification === 'verified' ? 'ok' : 'warn'}`}>
                  {contact.verification === 'verified' ? t('msg.contacts.verified') : t('msg.contacts.notVerified')}
                </span>
                {contact.verification === 'verified' || !mayAct ? null : (
                  <button
                    type="button"
                    className="btn"
                    data-testid={`verify-${contact.contactId}`}
                    onClick={() => setVerifying(verifying === contact.contactId ? null : contact.contactId)}
                  >
                    {verifying === contact.contactId ? t('msg.contacts.close') : t('msg.contacts.verify')}
                  </button>
                )}
              </div>
            ))
          )}

          {mayAct ? (
            <div className="rule-sub">
              <h3>{t('msg.contacts.addTitle')}</h3>
              <div className="form-row">
                <label className="field">
                  {t('msg.contacts.address')}
                  <input value={address} onChange={(event) => setAddress(event.target.value)} />
                </label>
                <label className="field">
                  {t('msg.contacts.name')}
                  <input value={displayName} onChange={(event) => setDisplayName(event.target.value)} />
                </label>
              </div>
              <p className="muted small">{t('msg.contacts.addNote')}</p>
              <div className="card-foot">
                <button
                  type="button"
                  className="btn"
                  data-testid="add-contact"
                  disabled={busy || !address.includes('@')}
                  onClick={() => {
                    onAdd(address.trim(), displayName.trim());
                    setAddress('');
                    setDisplayName('');
                  }}
                >
                  {t('msg.contacts.add')}
                </button>
              </div>
            </div>
          ) : (
            <p className="rule-line">{t('msg.contacts.roleCannot', { role: role.replace('_', ' ') })}</p>
          )}
        </>
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------

function administrationName(administrations: readonly Administration[], id: string): string {
  // The id rather than "unknown": a reviewer whose grant does not cover this
  // administration still needs something they can quote when they ask about it.
  return administrations.find((administration) => administration.id === id)?.name ?? id;
}

function sectionMessage(caught: unknown, t: Translate): string {
  if (!(caught instanceof ApiError)) return t('msg.error.read');
  if (caught.code === 'transport_error') return t('msg.error.transport');
  if (caught.isAbsent) return t('msg.error.absent');
  if (caught.status === 403) return t('msg.error.forbiddenRead');
  return t('msg.error.read');
}

function actionMessage(caught: unknown, t: Translate): string {
  if (!(caught instanceof ApiError)) return t('msg.error.action');
  if (caught.isStale) return t('msg.error.stale');
  if (caught.status === 403) return t('msg.error.forbidden');
  if (caught.isAbsent) return t('msg.error.gone');
  if (caught.status === 422) {
    const unverified = caught.fieldErrors.some((error) => error.code === 'unverified');
    if (unverified) return t('msg.error.unverified');
    return t('msg.error.refused', {
      fields: caught.fieldErrors.map((f) => `${f.field} (${f.code})`).join(', ') || t('msg.error.validation'),
    });
  }
  return caught.message;
}
